// Vendor Libs
import {difference, filter, get, includes, isEmpty, isEqual, map} from 'lodash';
import {push} from 'redux-first-history';
import servicesConfig from '../../config/services-config';
import {getQueryParams, getRouterParams} from './fetch-middleware';
import actionsServiceCaller from '../lib/app/actions-service-caller';

const massActionMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    const state = getState();

    next(action);

    const nextState = getState();
    const actions = {
        MASS_ACTION_ADD_TARGETS: () => handleTargetsUpdate(dispatch, action, state, nextState),
        MASS_ACTION_CLEAR_TARGETS: () => handleTargetsUpdate(dispatch, action, state, nextState),
        MASS_ACTION_REMOVE_TARGETS: () => handleTargetsUpdate(dispatch, action, state, nextState),
        MASS_ACTION_RESET: () => handleReset(dispatch, action),
        MASS_ACTION_SET_ACTIVE: () => handleSetActive(dispatch, action),
        MASS_ACTION_SET_CHILD_FORM_DATA: () => handleChildFormDataUpdate(dispatch, action, state),
        MASS_ACTION_SET_TARGETS: () => handleTargetsUpdate(dispatch, action, state, nextState),
        MASS_ACTION_SUBMIT: () => handleSubmit(dispatch, nextState),
        MASS_ACTION_SUBMIT_MEDIA_ITEM_ASYNC_REPORT: () => handleSubmitMediaItemAsyncReport(dispatch, action, state),
        RECORD_LOCKING_UPDATE: () => handleRecordLockingUpdate(dispatch, state, nextState),
        SEARCH_DATA_FETCH_FULFILLED: () => handleFetchFulfilled(dispatch, action, state, nextState),
        MASS_ACTION_SUBMIT_MEDIA_ITEM_DUPLICATE_TITLES_ASYNC_REPORT: () =>
            handleSubmitmediaItemDuplicateTitlesAsyncReport(dispatch, action, nextState),
    };

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function resetAllMassActionLocks(dispatch) {
    map(['mediaItems', 'unmappedMediaItems', 'mediaItemsEnhanced'], (component) => {
        dispatch({
            component: `${component}MassActions`,
            recordIds: [],
            serviceKey: 'mediaItems',
            type: 'RECORD_LOCKING_SET_LOCKED',
        });
    });
}

function handleSetActive(dispatch, {active, massActionName}) {
    if (!active) {
        dispatch({
            massActionName,
            selectAll: false,
            type: 'MASS_ACTION_SET_SELECT_ALL',
        });

        dispatch({
            massActionName,
            type: 'MASS_ACTION_CLEAR_TARGETS',
        });

        dispatch({
            type: 'SEARCH_DATA_SET_FILTER',
            targets: null,
            servicesDataKey: 'mediaItems',
        });

        dispatch({
            type: 'MAPPING_RESET',
            fullReset: false,
        });

        resetAllMassActionLocks(dispatch);
    } else {
        dispatch({
            component: `${massActionName}MassActions`,
            recordIds: [],
            serviceKey: 'mediaItems',
            type: 'RECORD_LOCKING_SET_LOCKED',
        });
    }
}

function handleTargetsUpdate(dispatch, {massActionName}, state, nextState) {
    const targets = get(state, 'massActionData.targets', []);
    const nextTargets = get(nextState, 'massActionData.targets', []);
    const targetIds = map(nextTargets, 'guid');
    const {location: {pathname} = {}, routerParams: {guid} = {}} = get(nextState, 'router', {});

    if (targets.length !== nextTargets.length || difference(targets, nextTargets).length) {
        dispatch({
            component: `${massActionName}MassActions`,
            recordIds: targetIds,
            serviceKey: 'mediaItems',
            type: 'RECORD_LOCKING_SET_LOCKED',
        });

        dispatch({
            targets: nextTargets,
            type: 'MASS_ACTION_TARGETS_UPDATE',
        });

        if (guid && !includes(targetIds, guid) && !isEmpty(targetIds)) {
            dispatch(push(`${pathname.substr(0, pathname.lastIndexOf('/'))}/${targetIds[0]}`));
        }
    }
}

function handleReset(dispatch) {
    resetAllMassActionLocks(dispatch);
}

function handleFetchFulfilled(dispatch, {query = {}, servicesDataKey}, state, nextState) {
    const {servicesSearchData: {[servicesDataKey]: {query: oldQuery = {}}} = {}} = state;
    const {massActionData: {active, massActionName} = {}} = nextState;

    if (active && massActionName === servicesDataKey && !isEqual(oldQuery, query)) {
        dispatch({
            massActionName,
            type: 'MASS_ACTION_CLEAR_TARGETS',
        });
    }
}

function handleRecordLockingUpdate(dispatch, state, nextState) {
    const {recordLocking: {lockedRecords: {mediaItems: lockedRecords = []} = {}} = {}} = state;
    const {massActionData: {massActionName, targets} = {}} = nextState;
    const {
        recordsToLock: {mediaItems: {[`${massActionName}MassActions`]: recordsToLock = []} = {}} = {},
        recordLocking: {lockedRecords: {mediaItems: nextLockedRecords = []} = {}},
    } = nextState;

    if (lockedRecords.length !== nextLockedRecords.length || difference(lockedRecords, nextLockedRecords).length) {
        const removedLockedRecords = filter(
            lockedRecords,
            (record) => !find(nextLockedRecords, 'recordId', record.recordId)
        );

        if (removedLockedRecords.length) {
            const targetsUnexpired = filter(targets, (target) => !find(removedLockedRecords, 'recordId', target));

            if (targetsUnexpired.length !== targets.length) {
                dispatch({
                    massActionName,
                    targetsUnexpired,
                    type: 'MASS_ACTION_SET_TARGETS',
                });
            }

            const recordsToLockUnexpired = filter(
                recordsToLock,
                (record) => !find(removedLockedRecords, 'recordId', record.recordId)
            );

            if (recordsToLockUnexpired.length !== recordsToLock.length) {
                dispatch({
                    component: `${massActionName}MassActions`,
                    recordIds: recordsToLockUnexpired,
                    serviceKey: 'mediaItems',
                    type: 'RECORD_LOCKING_SET_LOCKED',
                });
            }
        }
    }
}

function handleSubmit(dispatch, nextState) {
    const {massActionData: {childFormName: formName, massActionName, targets} = {}} = nextState;

    if (!isEmpty(targets)) {
        Promise.resolve(
            dispatch({
                type: 'FORM_REGISTER',
                formName,
            })
        ).then(() => {
            dispatch({
                submitted: true,
                type: 'MASS_ACTION_SET_SUBMITTED',
            });
            if (formName !== 'bulkMapShows' && formName !== 'bulkMapSpecials') {
                Promise.resolve(
                    dispatch({
                        type: 'FORM_SET_FIELD_VALUE',
                        fieldName: 'targets',
                        formName,
                        value: map(targets, 'guid'),
                    })
                ).then(() => {
                    Promise.resolve(
                        dispatch({
                            type: 'FORM_SUBMIT_FETCH',
                            formName,
                            async: true,
                        })
                    ).then(() => {
                        dispatch({
                            massActionName,
                            selectAll: false,
                            type: 'MASS_ACTION_SET_SELECT_ALL',
                        });
                        dispatch({
                            active: false,
                            massActionName,
                            type: 'MASS_ACTION_SET_ACTIVE',
                        });
                    });
                });
            } else {
                Promise.resolve(
                    dispatch({
                        type: 'MAPPING_BULK_MAP',
                    })
                ).then(() => {
                    dispatch({
                        massActionName,
                        selectAll: false,
                        type: 'MASS_ACTION_SET_SELECT_ALL',
                    });
                    dispatch({
                        active: false,
                        massActionName,
                        type: 'MASS_ACTION_SET_ACTIVE',
                    });
                    dispatch({
                        fullReset: true,
                        type: 'MAPPING_RESET',
                    });
                });
            }
        });
    }
}

function handleChildFormDataUpdate(dispatch, {childFormName}, state) {
    const {massActionData: {childFormName: childFormNameState} = {}} = state;

    if (childFormName === 'bulkMapSpecials' || childFormName === 'bulkMapShows') {
        dispatch({
            type: 'MAPPING_SET_BULK_MAPPING',
            bulkMapping: true,
            bulkMappingType: childFormName === 'bulkMapSpecials' ? 'SPECIALS' : 'SHOWS',
        });
    } else {
        dispatch({
            type: 'FORM_UNREGISTER',
            formName: childFormNameState,
        });
    }
}

function handleSubmitMediaItemAsyncReport(dispatch, {serviceDataKey: serviceHoldingQueryData}, state) {
    const formName = 'mediaItemAsyncReport'; // service key for async report
    const {servicesSearchData} = state;
    const {query} = servicesSearchData?.[serviceHoldingQueryData];

    const dispatchFetched = (_, response) => {
        const {headers} = response;
        const {location} = headers;

        return dispatch({
            type: 'SEARCH_DATA_FETCH_FULFILLED',
            path: servicesConfig[formName].route,
            query,
            searchData: location,
            servicesDataKey: formName,
        });
    };

    const dispatchError = (error) => {
        dispatch({
            type: 'SEARCH_DATA_FETCH_REJECTED',
            error,
            servicesDataKey: formName,
        });
    };
    const dispatchCancel = () => {
        dispatch({
            type: 'SEARCH_DATA_FETCH_CANCELED',
            servicesDataKey: formName,
        });
    };
    const config = {
        ...servicesConfig[serviceHoldingQueryData],
        ...servicesConfig[formName],
        routerKeys: undefined,
    };

    const actionParams = {
        dispatchCancel,
        dispatchFetched,
        dispatchError,
        servicesConfig: config,
    };

    dispatch({
        type: 'SEARCH_DATA_FETCH',
        servicesDataKey: formName,
    });

    return actionsServiceCaller(actionParams, getQueryParams(query, config));
}

function getDuplicateTitlesStoreQuery(servicesDataKey, {formData, router}) {
    const {formName, routerKeys} = servicesConfig[servicesDataKey];
    const serviceFormData = formName ? get(formData, [formName], {}) : {};
    const routerParams = getRouterParams(routerKeys, get(router, 'routerParams', {}));
    const rsql = {
        amsFranchise: {
            echostarContentType: serviceFormData?.values?.amsFranchise?.const?.echostarContentType,
        },
        service: {
            serviceName: serviceFormData?.values?.service?.const?.serviceName,
        },
    };

    return {
        path: routerParams.path,
        query: {
            rsql,
            operators: serviceFormData?.operators,
        },
    };
}

function handleSubmitmediaItemDuplicateTitlesAsyncReport(
    dispatch,
    {serviceDataKey: serviceHoldingQueryData},
    nextState
) {
    const formName = 'mediaItemDuplicateTitlesAsyncReport'; // service key for duplicate titles async report
    const {query} = getDuplicateTitlesStoreQuery(serviceHoldingQueryData, nextState);

    const dispatchFetched = (_, response) => {
        const {headers} = response;
        const {location} = headers;

        return dispatch({
            type: 'SEARCH_DATA_FETCH_FULFILLED',
            path: servicesConfig[formName].route,
            query,
            searchData: location,
            servicesDataKey: formName,
        });
    };

    const dispatchError = (error) => {
        dispatch({
            type: 'SEARCH_DATA_FETCH_REJECTED',
            error,
            servicesDataKey: formName,
        });
    };
    const dispatchCancel = () => {
        dispatch({
            type: 'SEARCH_DATA_FETCH_CANCELED',
            servicesDataKey: formName,
        });
    };
    const config = {
        ...servicesConfig[serviceHoldingQueryData],
        ...servicesConfig[formName],
        routerKeys: undefined,
    };

    const actionParams = {
        dispatchCancel,
        dispatchFetched,
        dispatchError,
        servicesConfig: config,
    };

    dispatch({
        type: 'SEARCH_DATA_FETCH',
        servicesDataKey: formName,
    });

    return actionsServiceCaller(actionParams, getQueryParams(query, config));
}

export default massActionMiddleware;
